import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useQuery } from "@apollo/client";
import { GET_DMA_AVAILABILITY } from "../../../graphql/dma-availability";
import { dmaData } from "../../../utils/minified-dma-data";
import "./styles.scss";

const normalizeZip = (value: string) => {
  const match = value.trim().match(/(\d{5})/);
  return match ? match[1] : "";
};

const getDmaForZip = (zip: string) => {
  if (!zip) return null;
  const lookup = (dmaData as any)?.[zip];
  const code = lookup?.DMA ?? lookup?.dma;
  return code ? String(code) : null;
};

export const HomeownerZipFieldset = ({
  zipCode,
  setZipCode,
  setDmaCode,
  setIsInServiceArea,
  setNextStep,
}: {
  zipCode: string;
  setZipCode: (value: string) => void;
  setDmaCode: (value: string | null) => void;
  setIsInServiceArea: (value: boolean | null) => void;
  setNextStep: () => void;
}) => {
  const { data, loading, error } = useQuery(GET_DMA_AVAILABILITY, {
    fetchPolicy: "cache-first",
  });
  const [touched, setTouched] = useState(false);

  const normalizedZip = useMemo(() => normalizeZip(zipCode), [zipCode]);
  const dmaCode = useMemo(() => getDmaForZip(normalizedZip), [normalizedZip]);
  const availableCodes = useMemo(
    () =>
      (data?.getDmaAvailability?.codes || [])
        .map((code: string) => String(code))
        .filter(Boolean),
    [data]
  );

  const isZipValid = normalizedZip.length === 5;
  const isKnownZip = Boolean(dmaCode);
  const isInServiceArea =
    dmaCode && availableCodes.length
      ? availableCodes.includes(String(dmaCode))
      : null;

  useEffect(() => {
    setDmaCode(dmaCode);
    setIsInServiceArea(isInServiceArea);
  }, [dmaCode, isInServiceArea, setDmaCode, setIsInServiceArea]);

  const handleContinue = () => {
    setTouched(true);
    if (!isZipValid || !isKnownZip) return;
    setNextStep();
  };

  return (
    <Box className="homeowner-zip-fieldset">
      <Typography variant="h5" className="form-title">
        Our curated designer network is expanding nationwide.
      </Typography>
      <Typography variant="body1" className="form-subtitle">
        Enter your zip code to explore availability in your area.
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        Please enter the ZIP CODE for the location you'd like to match with a designer in. For example,
        if you live in Atlanta but are working on a beach house in Florida, enter the zip code where you
        want your interior designer to be located. In most cases, you may choose to match with an
        Interior Designer that is located where you live full-time - most of the designers in our network
        have the expertise to work across state lines.
      </Typography>

      {error && (
        <Alert severity="warning">
          Unable to load availability list. You can still continue.
        </Alert>
      )}

      <TextField
        label="ZIP Code"
        value={zipCode}
        onChange={(event) => setZipCode(event.target.value)}
        placeholder="Enter 5-digit ZIP"
        inputProps={{ inputMode: "numeric", maxLength: 10 }}
        fullWidth
        onBlur={() => setTouched(true)}
        helperText={
          touched && (!isZipValid || !isKnownZip)
            ? "Please enter a valid 5-digit ZIP code."
            : " "
        }
        error={touched && (!isZipValid || !isKnownZip)}
      />

      {loading && (
        <Box display="flex" alignItems="center" gap={1}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            Checking availability…
          </Typography>
        </Box>
      )}

      <Box className="actions">
        <Button
          variant="contained"
          className="submit-btn"
          onClick={handleContinue}
          disabled={loading}
        >
          Continue
        </Button>
      </Box>
    </Box>
  );
};

export default HomeownerZipFieldset;
